import React, { createContext, useContext, useState } from 'react';

/**
 * Globalny kontekst integratora dla super-admina: wybrana w sidebarze firma, w którą
 * admin się "wciela". Id leci nagłówkiem X-Integrator-Context przy każdym request'cie
 * (interceptor w api/client.ts), a backend (AccessGuard) zawęża scope do tej firmy.
 * Zakładki czysto adminowe (/api/admin/**) nie podlegają kontekstowi.
 */
export interface IntegratorContextValue {
  id: string;
  name: string;
  nip: string | null;
}

const LS_KEY = 'mm_integrator_context';

export const readIntegratorContext = (): IntegratorContextValue | null => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as IntegratorContextValue) : null;
  } catch {
    return null;
  }
};

interface CtxShape {
  integrator: IntegratorContextValue | null;
  setIntegrator: (i: IntegratorContextValue | null) => void;
}

const Ctx = createContext<CtxShape>({ integrator: null, setIntegrator: () => {} });

export const IntegratorContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [integrator, setState] = useState<IntegratorContextValue | null>(readIntegratorContext);

  const setIntegrator = (i: IntegratorContextValue | null) => {
    if (i) {
      localStorage.setItem(LS_KEY, JSON.stringify(i));
    } else {
      localStorage.removeItem(LS_KEY);
    }
    setState(i);
    // Strony ładują dane na mount i nie subskrybują kontekstu — twardy reload
    // gwarantuje, że każdy widok przeładuje się w nowym kontekście.
    window.location.reload();
  };

  return <Ctx.Provider value={{ integrator, setIntegrator }}>{children}</Ctx.Provider>;
};

export const useIntegratorContext = () => useContext(Ctx);
